import { ThresholdDirection } from "@/common/model";

const CURRENCIES = new Set(["USD", "EUR", "GBP", "JPY", "CNY", "INR"]);

/**
 * A metric value for display, auto-compacted (1,284 · 12.9K · $4.2M) with its unit: currency
 * units become a currency format, anything else trails the number ("4.2K users").
 */
export function formatMetric(
	value: number | null | undefined,
	unit?: string | null,
): string {
	if (value == null) return "—";
	const compact = Math.abs(value) >= 10_000;
	if (unit && CURRENCIES.has(unit)) {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: unit,
			notation: compact ? "compact" : "standard",
			maximumFractionDigits: compact ? 1 : 0,
		}).format(value);
	}
	const number = new Intl.NumberFormat(undefined, {
		notation: compact ? "compact" : "standard",
		maximumFractionDigits: Math.abs(value) < 10 ? 2 : compact ? 1 : 0,
	}).format(value);
	return unit ? `${number} ${unit}` : number;
}

/** The signed relative change from `previous` to `value`, e.g. "+12%", or null when it can't be said. */
export function formatDelta(
	value: number | null | undefined,
	previous: number | null | undefined,
): string | null {
	if (value == null || previous == null || previous === 0) return null;
	const change = (value - previous) / Math.abs(previous);
	const percent = Math.round(change * 100);
	if (percent === 0) return "±0%";
	return `${percent > 0 ? "+" : "−"}${Math.abs(percent)}%`;
}

/**
 * Whether a change is good, bad or neither: up is bad when the metric's thresholds are crossed from
 * below (cost, errors), good when crossed from above. Without thresholds a change is just a change.
 */
export function deltaTone(
	value: number | null | undefined,
	previous: number | null | undefined,
	direction: ThresholdDirection | null | undefined,
): "good" | "bad" | "neutral" {
	// A change that rounds to ±0% reads as no change; colouring it would contradict the label.
	if (
		value == null ||
		previous == null ||
		!direction ||
		formatDelta(value, previous) === "±0%" ||
		value === previous
	)
		return "neutral";
	const up = value > previous;
	return up === (direction === ThresholdDirection.ABOVE) ? "bad" : "good";
}
